import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "The main advantage of SMART goals over vague wishes is that they are:",
    options: [
      "More difficult and challenging",
      "Specific, measurable, and action-oriented",
      "Guaranteed to work",
      "The same thing, just with a different name"
    ],
    correctAnswer: 1,
    explanation: "SMART goals provide clarity and structure. They're specific enough to guide action, measurable so you can track progress, and achievable through concrete steps."
  },
  {
    question: "When transforming 'I want to be healthier' into a SMART goal, which element is MOST important to add?",
    options: [
      "Making it harder to increase willpower",
      "Specific, measurable outcomes like 'exercise 3 times per week for 30 minutes'",
      "Telling more people about your goal",
      "Waiting until the perfect time to start"
    ],
    correctAnswer: 1,
    explanation: "Specificity and measurability transform vague wishes into actionable plans. You'll know exactly what you're working toward and how to measure success."
  },
  {
    question: "Which of these is an obstacle to goal achievement, rather than a barrier to overcome?",
    options: [
      "Lack of resources or skills needed",
      "Time constraints or competing priorities",
      "Believing the goal is impossible before trying",
      "Environmental factors that make progress difficult"
    ],
    correctAnswer: 2,
    explanation: "Internal beliefs ('impossible before trying') differ from external obstacles. They're actually within your control through perspective shifts and building skills."
  },
  {
    question: "An effective action plan for a goal should include:",
    options: [
      "Only the end goal, not the steps",
      "Specific steps, timelines, and strategies to overcome known obstacles",
      "Waiting to plan until you're completely motivated",
      "Keeping it vague so you stay flexible"
    ],
    correctAnswer: 1,
    explanation: "Detailed action plans with specific steps and contingencies for obstacles dramatically increase success rates. They turn inspiration into implementation."
  },
  {
    question: "If you encounter an obstacle while pursuing a SMART goal, the most productive response is to:",
    options: [
      "Abandon the goal immediately",
      "Blame external circumstances for failure",
      "Problem-solve and adjust your strategy or action plan",
      "Feel discouraged and give up"
    ],
    correctAnswer: 2,
    explanation: "Obstacles are normal, not signs of failure. Effective goal-achievers adjust tactics while maintaining commitment. This flexibility is key to success."
  }
];

const EmotioPsychoRationalModule5QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('emotioPsychoRationalM5');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/emotio-psycho-rational-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 5 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                Creating SMART goals that are actionable
              </li>
              <li>
                Transforming vague wishes into concrete plans
              </li>
              <li>
                Overcoming obstacles and persisting toward goals
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Apply SMART goal-setting to your personal wellness and life goals!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Emotio-Psycho-Rational Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left emotio-psycho-rational-panel">
          <div className="module-header">
            <h1>Module 5 Quiz</h1>
            <p className="module-subtitle">Integrating Emotio-Psycho-Rational Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about integrating emotio-psycho-rational wellness.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentQuestion === 0 ? handleExit : handleBack}
            >
              {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
            </button>
            
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalModule5QuizPage;


